import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { ArrowLeft, ShieldCheck, UserCircle2, MessageCircle, Award, Lock, BadgeCheck, MessageSquare, HeartHandshake, Share2 } from "lucide-react";
import { trackWhatsApp } from "../lib/beacon";
import { shareProfessionalCard } from "../lib/shareCard";
import { StarRating } from "../components/fp/StarRating";

const SUPPORT_WA = process.env.REACT_APP_SUPPORT_WA || "+5511996420785";
const digits = (s) => (s || "").replace(/[^0-9]/g, "");

const TIER_STYLES = {
  ouro:   { label: "Ouro",   ring: "ring-[#D4A017]", bg: "bg-gradient-to-r from-[#F1C232] to-[#D4A017] text-[#3B2F0B]" },
  prata:  { label: "Prata",  ring: "ring-slate-400", bg: "bg-gradient-to-r from-slate-300 to-slate-500 text-slate-900" },
  bronze: { label: "Bronze", ring: "ring-amber-700", bg: "bg-gradient-to-r from-[#CD7F32] to-[#8B4513] text-white" },
};

const WD = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

/**
 * Detalhe público do profissional Filho Presente — mesmo formato do
 * RAF Direct: SEM valor exposto, SEM botão de reserva direta. A família
 * conversa com o consultor de plantão, que valida horário, envia link de
 * pagamento e libera o profissional.
 */
export default function FPProfessionalDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onCall, setOnCall] = useState(null);
  const [reviews, setReviews] = useState({ stats: { count: 0, avg: null }, reviews: [] });
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    api.get(`/fp/professionals/${id}`).then((r) => setP(r.data))
      .catch(() => toast.error("Profissional não encontrado"))
      .finally(() => setLoading(false));
    api.get(`/fp/reviews/professional/${id}`).then((r) => setReviews(r.data)).catch(() => {});
    api.get("/public/on-call").then((r) => setOnCall(r.data)).catch(() => {});
  }, [id]);

  // Iter 68.66 — Se o pro NÃO serve FP efetivamente (RAF-origin, sem
  // categoria geriátrica), o OG amber `/api/og/fp-professional/{id}.svg`
  // retorna 404 (gate correto — selo FP é exclusivo de quem atende idoso).
  // Nesse caso usa a variante RAF pra ainda permitir compartilhar o card
  // do profissional sem gerar 404.
  const servesFpEffective = Boolean(p?.serves_fp_effective ?? (p?.source === "fp"));

  const onShare = async () => {
    if (sharing || !p) return;
    setSharing(true);
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL || "";
      const profileUrl = `${window.location.origin}/filho-presente/profissionais/${p.id}`;
      const res = await shareProfessionalCard({
        apiBase, professionalId: p.id,
        displayName: p.display_name, profileUrl,
        variant: servesFpEffective ? "fp" : "raf",
      });
      const label = servesFpEffective ? "Profissional Filho Presente" : "Perfil";
      if (res.mode === "shared") toast.success(`${label} compartilhado.`);
      else if (res.mode === "downloaded-and-copied") toast.success("Imagem baixada e link copiado — cole no Instagram/LinkedIn.");
      else if (res.mode === "downloaded") toast.success("Imagem baixada. Poste onde quiser!");
      else if (res.mode === "cancelled") { /* silêncio */ }
    } catch (e) {
      toast.error(e?.message || "Não foi possível gerar o card agora.");
    } finally { setSharing(false); }
  };

  if (loading) return <div className="p-10 text-slate-500">Carregando…</div>;
  if (!p) return <div className="p-10">Profissional não encontrado. <Link to="/filho-presente/profissionais" className="text-[#0B2B62] underline">Voltar</Link></div>;

  const tier = (p.certification_tier || "").toLowerCase();
  const style = TIER_STYLES[tier];
  const target = digits(onCall?.online ? (onCall.phone || SUPPORT_WA) : SUPPORT_WA);
  const consultorName = onCall?.online && onCall?.first_name ? onCall.first_name : "consultor RAF";
  // Iter 68.66.f — contexto pro consultor (nome + ref curto do pro)
  const proRef = p.id ? String(p.id).slice(0, 8) : "";
  const msg = `Olá, ${consultorName}. Vi o perfil de *${p.display_name || "profissional"}* no Filho Presente — Ref: FP-PRO-${proRef}. Gostaria de agendar.`;
  const waUrl = `https://wa.me/${target}?text=${encodeURIComponent(msg)}`;
  const categories = p.categories && p.categories.length ? p.categories : (p.category ? [p.category] : []);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="fp-pro-detail">
      <Link to="/filho-presente/profissionais" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#0B2B62]">
        <ArrowLeft className="w-4 h-4" /> Todos os profissionais
      </Link>

      <div className="mt-4 grid md:grid-cols-3 gap-6 md:gap-8 items-start">
        {/* Perfil */}
        <div className="md:col-span-2 text-left">
          <div className="flex items-start gap-5">
            {p.photo_url ? (
              <img src={p.photo_url} alt=""
                className={`w-24 h-24 rounded-full object-cover border-4 border-white shadow-md ${style ? `ring-4 ring-offset-2 ${style.ring}` : ""}`} />
            ) : (
              <div className="w-24 h-24 rounded-full bg-slate-100 flex items-center justify-center text-slate-400"><UserCircle2 className="w-10 h-10" /></div>
            )}
            <div>
              {p.council_type && (
                <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold inline-flex items-center gap-1">
                  <ShieldCheck className="w-3 h-3" /> {p.council_type}{p.council_state ? ` · ${p.council_state}` : ""}
                </div>
              )}
              <div className="flex items-center gap-2 flex-wrap mt-1">
                <h1 className="font-display font-bold text-[#0B2B62] text-2xl md:text-3xl">{p.display_name}</h1>
                <span title="Perfil auditado e aprovado pela curadoria RAF Direct."
                  data-testid="fp-pro-verified-badge"
                  className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-600 text-white ring-1 ring-emerald-700 shadow-sm">
                  <BadgeCheck className="w-3.5 h-3.5" strokeWidth={2.5} /> Perfil Verificado
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {categories.map((c) => (
                  <span key={c} className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 border border-slate-200">{c}</span>
                ))}
              </div>
              {p.specialty && <div className="text-xs text-slate-500 mt-2">{p.specialty}</div>}
              {p.source && (
                <span className={`inline-block mt-2 text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded ${
                  p.source === "fp" ? "bg-amber-500 text-white" : "bg-slate-900 text-white"}`}>
                  Origem {p.source === "fp" ? "Filho Presente" : "RAF Direct"}
                </span>
              )}
            </div>
          </div>

          {p.bio && (
            <div className="mt-6">
              <h2 className="font-display font-bold text-[#0B2B62] text-lg">Sobre</h2>
              <p className="mt-2 text-slate-700 whitespace-pre-line leading-relaxed">{p.bio}</p>
            </div>
          )}

          {(p.modalities?.length > 0) && (
            <div className="mt-6">
              <h2 className="font-display font-bold text-[#0B2B62] text-lg">Modalidades</h2>
              <div className="flex flex-wrap gap-2 mt-2">
                {p.modalities.map((m) => <span key={m} className="text-xs bg-amber-50 text-amber-700 border border-amber-100 px-2.5 py-1 rounded">{m}</span>)}
              </div>
            </div>
          )}

          {p.availability?.length > 0 && (
            <div className="mt-6">
              <h2 className="font-display font-bold text-[#0B2B62] text-lg">Horários disponíveis</h2>
              <p className="text-xs text-slate-500 mt-1">O consultor confirma o slot final antes do pagamento.</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
                {p.availability.map((a, i) => (
                  <div key={i} className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs">
                    <div className="font-medium text-slate-700">{WD[a.weekday]}</div>
                    <div className="text-slate-500">{a.start} - {a.end}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Avaliações */}
          <div className="mt-8" data-testid="fp-pro-reviews">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display font-bold text-[#0B2B62] text-lg flex items-center gap-1.5">
                <MessageSquare className="w-4 h-4" /> Avaliações
              </h2>
              {reviews.stats.count > 0 && (
                <div className="flex items-center gap-2">
                  <StarRating value={reviews.stats.avg || 0} readOnly size={18} />
                  <div className="text-sm font-semibold text-[#0B2B62]">
                    {reviews.stats.avg?.toFixed(1)} <span className="text-xs text-slate-500 font-normal">· {reviews.stats.count}</span>
                  </div>
                </div>
              )}
            </div>
            {reviews.reviews.length === 0 ? (
              <div className="text-sm text-slate-500">Ainda não há avaliações. Após a consulta, a família pode avaliar aqui.</div>
            ) : (
              <div className="space-y-3">
                {reviews.reviews.slice(0, 6).map((r) => (
                  <div key={r.id} className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <StarRating value={r.rating} readOnly size={14} />
                      <div className="text-xs font-semibold text-slate-700">{r.family_name}</div>
                      <div className="text-[10px] text-slate-400 ml-auto">
                        {new Date(r.created_at).toLocaleDateString("pt-BR")}
                      </div>
                    </div>
                    {r.comment && <p className="text-sm text-slate-700 mt-2 leading-relaxed">{r.comment}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar consultor */}
        <aside className="md:sticky md:top-24 h-fit bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          {style && (
            <span className={`inline-flex items-center gap-1 text-[10px] font-black px-2 py-1 rounded-full uppercase tracking-widest ${style.bg}`}>
              <Award className="w-3 h-3" /> Selo {style.label} · Validado RAF
            </span>
          )}
          <h3 className="mt-3 font-display font-bold text-[#0B2B62] text-lg flex items-center gap-2">
            <HeartHandshake className="w-5 h-5 text-amber-500" /> Agendamento pelo consultor
          </h3>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed">
            O agendamento e o pagamento acontecem pelo <b>consultor RAF de plantão</b>. Ele valida horário, envia o link seguro de pagamento e libera a sala/local. Reembolso em até <b>3 dias</b> após o horário, estorno em <b>72h úteis</b>.
          </p>
          <a href={waUrl} target="_blank" rel="noopener noreferrer"
            data-testid="fp-wa-consultant"
            onClick={() => trackWhatsApp("fp_prof_detail", { prof_id: p.id })}
            className="mt-4 w-full inline-flex items-center justify-start gap-2 px-4 py-3 rounded-md bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-sm">
            <MessageCircle className="w-4 h-4 shrink-0" /> Falar com consultor no WhatsApp
          </a>
          <button type="button" onClick={onShare} disabled={sharing}
            data-testid="fp-share-badge-btn"
            title={servesFpEffective
              ? "Baixa um card 1080x1080 do profissional Filho Presente — pronto para Instagram, LinkedIn e WhatsApp."
              : "Baixa um card 1080x1080 do perfil — pronto para Instagram, LinkedIn e WhatsApp."}
            className={`mt-2 w-full inline-flex items-center justify-start gap-2 px-4 py-2.5 rounded-md border-2 font-bold text-sm disabled:opacity-50 ${
              servesFpEffective
                ? "border-amber-500 text-amber-800 hover:bg-amber-50"
                : "border-emerald-600 text-emerald-800 hover:bg-emerald-50"
            }`}>
            <Share2 className="w-4 h-4" />
            {sharing
              ? "Gerando card…"
              : servesFpEffective
                ? "Compartilhar Profissional Filho Presente"
                : "Compartilhar Perfil"}
          </button>

          <div className="mt-4 pt-4 border-t border-slate-100 space-y-2 text-[11px] text-slate-500">
            <div className="flex items-start gap-1.5">
              <Lock className="w-3 h-3 mt-0.5 shrink-0" />
              <span>Pagamento antecipado pelo Stripe (cartão ou PIX), centralizado na RAF — nunca por PIX pessoal.</span>
            </div>
            <div className="flex items-start gap-1.5">
              <ShieldCheck className="w-3 h-3 mt-0.5 shrink-0" />
              <span><b>Reembolso:</b> em até <b>3 dias</b> após o horário; estorno em <b>72h úteis</b>.</span>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
